import { useEffect, useRef, useState } from 'react';
import { endOfMonth, parseISO, startOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Checkbox } from '@components/ui/checkbox';
import { Form, FormField } from '@components/ui/form';
import { Textarea } from '@components/ui/form/textarea';
import { SchedulerShell } from '@components/scheduling/shell';
import { PhoneTimeRange } from '@components/scheduling/phoneTimeRange';
import { TimeRange } from '@components/scheduling/timeRangePicker/types';
import { useInternalScheduler } from '@hooks/scheduler/internal';
import { useSchedulerSync } from '@hooks/scheduler/sync';
import { useAssignForeman } from '@hooks/scheduler/assign';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { isExistingMultiDay } from '@contexts/scheduler/sidebar/helpers';
import { formatTenantTime, formatTime, shortDate, shortDay, shortDayDate } from '@utils/i18n';
import { parseTenantDateTime, toTenantDate } from '@utils/timeZone';
import { parseJobTitle } from '@utils/textHelpers';
import { getShift } from '@utils/schedule/tenantShift';
import { cn } from '@utils/cn';
import { Schedule } from '@root/globalTypes';
import { InternalScheduleDialogProps } from '../types';
import { InternalScheduleActions } from '../actions';
import { OverlapScheduleDialog } from '../action/overlap';
import { SpecialtyOverrideDialog } from '../action/specialty';
import { overlaps, roundHours, toForeman, toWindow } from './helpers';
import { PhoneDateBar } from './dateBar';
import { PhoneFieldUser } from './fieldUser';
import { PhoneCrew } from './crew';
import { PhoneEquipment } from './equipment';
import { PhoneSection } from './section';
import { PhoneConfirmSheet } from './confirmSheet';
import { PhoneConflictsSheet } from './conflictsSheet';
import { PhoneConflictItem } from './conflictsSheet/types';
import { PhoneDiscardSheet } from './discardSheet';
import { PhoneSent } from './sent';
import { PhoneSentProps } from './sent/types';
import { PhoneMultiDay } from './multiDay';
import { DEFAULT_MULTI_DAY } from './multiDay/constants';
import { dayKey, formatDays, getConflicts, getIncludedDays } from './multiDay/helpers';
import { BusyHit, BusyLookup, MultiDayState, ResourceKind } from './multiDay/types';
import { PhoneConflictCalendar } from './conflictCalendar';
import { ConflictCalendarTarget } from './conflictCalendar/types';
import { useGetSchedulesAvailability } from '@services/schedules/availability';

type Picker = 'crew' | 'equipment' | null;
type Mode = 'single' | 'multi';
type SentInfo = Omit<PhoneSentProps, 'onBack' | 'onEdit'>;

export default function MobileInternalScheduleDialog({
  open,
  setOpen,
  type,
  schedule,
  jobId,
  initialDate,
  activeId,
  readOnly,
  initialParams = {},
}: InternalScheduleDialogProps) {
  const { t } = useTranslation('app');
  const popoverRef = useRef<HTMLDivElement>(null);
  const fieldUserRef = useRef<HTMLDivElement>(null);
  const pendingSent = useRef<SentInfo>(undefined);
  const [draftTime, setDraftTime] = useState<TimeRange>(getShift);
  const [picker, setPicker] = useState<Picker>(null);
  const [swapId, setSwapId] = useState<string>();
  const [discardOpen, setDiscardOpen] = useState(false);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const [sent, setSent] = useState<SentInfo>();
  const [saved, setSaved] = useState<Schedule>();
  const [edited, setEdited] = useState<Schedule>();
  const [mode, setMode] = useState<Mode>('single');
  const [multiDay, setMultiDay] = useState<MultiDayState>(DEFAULT_MULTI_DAY);
  const [popMonth, setPopMonth] = useState<Date>();
  const [calendarTarget, setCalendarTarget] = useState<ConflictCalendarTarget>();
  const [sendCheck, setSendCheck] = useState(false);
  const current = edited || schedule;
  const assignForeman = useAssignForeman();
  const {
    crewMembers,
    equipmentMembers,
    conflictsByUserId,
    conflictsByAssetId,
    currentSchedule,
    relatedMultischedules,
  } = useSchedulerSidebar();
  const pendingAction = useRef<'send' | 'submit'>('submit');

  const close = (keepEdited = false) => {
    if (!keepEdited) setEdited(undefined);
    setSent(undefined);
    setDiscardOpen(false);
    setConflictsOpen(false);
    setPicker(null);
    setSendCheck(false);
    setCalendarTarget(undefined);
    setMode('single');
    setMultiDay(DEFAULT_MULTI_DAY);
    setOpen(false);
  };

  const {
    form,
    onSubmit,
    locked,
    data,
    schedulesFetching,
    schedulesLoading,
    overlap,
    setOverlap,
    conflicts,
    specialtyOverride,
    setSpecialtyOverride,
    rawDate,
    setRawDate,
    active,
    jobSubtitle,
    timeRange,
    setActiveTime,
    scheduler,
  } = useInternalScheduler({
    open,
    setOpen: (value) => (value ? setOpen(true) : close()),
    type,
    schedule: current,
    jobId,
    initialDate: edited ? parseTenantDateTime(edited.startsAt) : initialDate,
    readOnly,
    initialParams,
    allUsersQuery: true,
    onSent: (resp) => {
      setSaved({ ...current, ...resp.data, task: current?.task });
      setSent(pendingSent.current);
    },
  });
  const { setValue, watch } = form;

  useSchedulerSync({
    date: rawDate,
    foremen: data?.records,
    withoutTeam: data?.withoutTeam,
    outOfScopeRecords: data?.outOfScopeRecords,
    modal: true,
    schedule: current,
    activeId,
  });

  const activeSchedule = active.schedule.id ? (active.schedule as Schedule) : undefined;
  const foremanId = activeSchedule?.foreman?.id || '';
  const foremanRecord = data?.records.find((el) => el.id === foremanId);
  const disabled = Boolean(readOnly);
  const isNew = !current?.id || current.id === 'new';
  const isDraft = current?.status === 'draft';
  const range = timeRange || draftTime;
  const slot = toWindow(scheduler.date || rawDate, range);
  const hours = roundHours(Math.max(0, range.end - range.start));
  const activeCrew = crewMembers.filter((el) => !el.discardedAt && !el.userDeactivated);
  const people = (foremanId ? 1 : 0) + activeCrew.length;
  const manHours = activeSchedule ? Math.round(hours * people * 10) / 10 : 0;
  const timeText = [range.start, range.end]
    .map((minutes) => formatTime(new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60)))
    .join(' - ');

  const assign = (record?: Parameters<typeof toForeman>[0]) =>
    assignForeman(record && toForeman(record), { day: scheduler.date || rawDate, time: draftTime });

  const openPicker = (kind: Picker, id?: string) => {
    setConflictsOpen(false);
    setSwapId(id);
    setPicker(kind);
  };

  const isMulti = mode === 'multi';
  const originalDay = !isNew && current?.startsAt ? dayKey(toTenantDate(current.startsAt)) : '';
  const canMultiDay =
    !disabled &&
    (isNew ||
      (Boolean(currentSchedule?.id) &&
        !current?.task?.multischedule &&
        !isExistingMultiDay(relatedMultischedules)));
  const includedDays = isMulti ? getIncludedDays(multiDay) : [dayKey(scheduler.date || rawDate)];
  const firstDay = includedDays[0];
  const crewIds = activeCrew.map((el) => el.userId);
  const assetIds = equipmentMembers
    .filter((el) => !el.equipmentDeactivated)
    .map((el) => el.assetId);
  const multiAvailability = useGetSchedulesAvailability({
    from: firstDay,
    to: includedDays.at(-1),
    scheduleId: current?.id,
    taskType: type?.value,
    enabled: open && isMulti,
  });
  const monthAvailability = useGetSchedulesAvailability({
    from: popMonth && dayKey(startOfMonth(popMonth)),
    to: popMonth && dayKey(endOfMonth(popMonth)),
    scheduleId: current?.id,
    taskType: type?.value,
    userIds: [foremanId, ...crewIds].filter(Boolean),
    assetIds,
    enabled: open && !isMulti && Boolean(foremanId || crewIds.length || assetIds.length),
  });
  const lookup: BusyLookup = {
    availability: multiAvailability.data,
    days: new Set(includedDays),
    range,
  };
  const selected: Array<[ResourceKind, string]> = [
    ...(foremanId ? [['users', foremanId] as [ResourceKind, string]] : []),
    ...crewIds.map((id) => ['users', id] as [ResourceKind, string]),
    ...assetIds.map((id) => ['assets', id] as [ResourceKind, string]),
  ];
  const busyDates = (source: BusyLookup) =>
    new Set(
      selected.flatMap(([kind, id]) => getConflicts(source, kind, id).map((hit) => hit.date)),
    );
  const multiDots = isMulti ? busyDates(lookup) : new Set<string>();
  const monthDots = monthAvailability.data
    ? [
        ...busyDates({
          availability: monthAvailability.data,
          days: new Set(Object.values(monthAvailability.data.schedules).map((el) => el.date)),
          range,
        }),
      ].map((date) => parseISO(date))
    : [];
  const showConflicts = (name: string, hits: BusyHit[]) => setCalendarTarget({ name, hits });
  const multiProps = isMulti ? { lookup, onShowConflicts: showConflicts } : undefined;
  const multiLines = (kind: ResourceKind, id: string) =>
    getConflicts(lookup, kind, id).map((hit) => ({
      key: `${hit.scheduleId}-${hit.date}`,
      when: `${shortDay(parseISO(hit.date))}, ${shortDate(parseISO(hit.date))} · ${formatTenantTime(hit.startsAt)} - ${formatTenantTime(hit.endsAt)}`,
      text: [hit.taskName, hit.job].filter(Boolean).join(' · '),
    }));

  useEffect(() => {
    if (isMulti && firstDay) setRawDate(parseISO(firstDay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMulti, firstDay]);

  const foremanHits = (foremanRecord?.schedules || []).filter(
    (item) => item.id !== activeSchedule?.id && overlaps(slot, [item]),
  );
  const singleConflictItems: PhoneConflictItem[] = [
    ...(foremanHits.length && foremanRecord
      ? [
          {
            key: `field-${foremanRecord.id}`,
            name: foremanRecord.fullName,
            lines: foremanHits.map((item) => ({
              key: item.id,
              when: [item.startsAt, item.endsAt].map(formatTenantTime).join(' - '),
              text: [item.task?.name, item.task?.job && parseJobTitle(item.task.job)]
                .filter(Boolean)
                .join(' · '),
            })),
            onSwap: () => {
              setConflictsOpen(false);
              fieldUserRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            },
          },
        ]
      : []),
    ...activeCrew
      .filter((el) => conflictsByUserId[el.userId]?.length)
      .map((el) => ({
        key: `crew-${el.userId}`,
        name: el.name,
        lines: conflictsByUserId[el.userId].map((hit) => ({
          key: hit.scheduleId,
          when: [hit.startsAt, hit.endsAt].map(formatTenantTime).join(' - '),
          text: [hit.name, hit.projectIdentifier].filter(Boolean).join(' · '),
        })),
        onSwap: readOnly ? undefined : () => openPicker('crew', el.userId),
      })),
    ...equipmentMembers
      .filter((el) => !el.equipmentDeactivated && conflictsByAssetId[el.assetId]?.length)
      .map((el) => ({
        key: `asset-${el.assetId}`,
        name: el.name,
        lines: conflictsByAssetId[el.assetId].map((hit) => ({
          key: hit.scheduleId,
          when: [hit.startsAt, hit.endsAt].map(formatTenantTime).join(' - '),
          text: [hit.name, hit.projectIdentifier].filter(Boolean).join(' · '),
        })),
        onSwap: readOnly ? undefined : () => openPicker('equipment', el.assetId),
      })),
  ];
  const multiConflictItems: PhoneConflictItem[] = [
    ...(foremanId
      ? [
          {
            key: `field-${foremanId}`,
            name: foremanRecord?.fullName || activeSchedule?.foreman?.fullName || '',
            lines: multiLines('users', foremanId),
            onSwap: () => {
              setConflictsOpen(false);
              setSendCheck(false);
              fieldUserRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            },
          },
        ]
      : []),
    ...activeCrew.map((el) => ({
      key: `crew-${el.userId}`,
      name: el.name,
      lines: multiLines('users', el.userId),
      onSwap: readOnly ? undefined : () => openPicker('crew', el.userId),
    })),
    ...equipmentMembers
      .filter((el) => !el.equipmentDeactivated)
      .map((el) => ({
        key: `asset-${el.assetId}`,
        name: el.name,
        lines: multiLines('assets', el.assetId),
        onSwap: readOnly ? undefined : () => openPicker('equipment', el.assetId),
      })),
  ].filter((item) => item.lines.length);
  const conflictItems = isMulti ? multiConflictItems : singleConflictItems;

  const submit = () => {
    setValue('pickedDates', isMulti ? includedDays : []);
    void form.handleSubmit(onSubmit)();
  };
  const send = () => {
    rememberSent();
    setValue('actionType', 'saveAndSend');
    submit();
  };
  const saveDraft = () => {
    setDiscardOpen(false);
    if (!isNew && !isDraft) setValue('status', 'draft');
    setValue('actionType', '');
    submit();
  };
  const rememberSent = () => {
    pendingSent.current = {
      people,
      foreman: foremanRecord?.fullName,
      date: isMulti
        ? `${formatDays(includedDays)} · ${t('scheduling.phone.days', { count: includedDays.length })}`
        : `${shortDay(scheduler.date || rawDate)}, ${shortDate(scheduler.date || rawDate)}`,
      time: timeText,
      confirmed: Boolean(watch('confirmed')),
    };
  };
  const requestClose = () => {
    if (readOnly || sent || !activeSchedule) close();
    else setDiscardOpen(true);
  };

  const tabClass = (on: boolean) =>
    cn(
      'h-10 rounded-lg border-[1.5px] text-[15px] disabled:opacity-60',
      on
        ? 'border-primary-100 bg-primary-50 font-semibold text-primary-600'
        : 'border-neutral-50 bg-neutral-50 text-neutral-500',
    );

  return (
    <Form {...form}>
      <SchedulerShell
        open={open}
        onOpenChange={(value) => (value ? setOpen(true) : close())}
        onClose={requestClose}
        title={t('schedule.team', { team: type?.name })}
        subtitle={jobSubtitle}
        popoverRef={popoverRef}
        toolbar={
          <div className="grid grid-cols-2 gap-2 border-b border-neutral-100 bg-neutral-50/60 px-3 py-2.5">
            <button
              type="button"
              aria-pressed={!isMulti}
              onClick={() => setMode('single')}
              className={tabClass(!isMulti)}>
              {t('scheduling.phone.singleDay')}
            </button>
            <button
              type="button"
              disabled={!canMultiDay}
              aria-pressed={isMulti}
              onClick={() => {
                if (isMulti) return;
                const start = dayKey(scheduler.date || rawDate);
                setMultiDay({
                  ...DEFAULT_MULTI_DAY,
                  start,
                  overrides: isNew ? {} : { [start]: true },
                });
                setMode('multi');
              }}
              className={tabClass(isMulti)}>
              {t('scheduling.phone.multipleDays')}
            </button>
          </div>
        }
        overlay={
          <>
            <PhoneConfirmSheet />
            {sent && (
              <PhoneSent
                {...sent}
                onBack={() => close()}
                onEdit={() => {
                  close(true);
                  setEdited(saved);
                  setTimeout(() => setOpen(true), 0);
                }}
              />
            )}
          </>
        }
        footer={
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const draft = isNew ? !watch('actionType') : isDraft || watch('status') === 'draft';
              if (isMulti && conflictItems.length && !draft) {
                pendingAction.current = 'submit';
                setSendCheck(true);
              } else submit();
            }}
            className="flex flex-col gap-2">
            {(['forceOverlapping', 'foremanId', 'startsAt', 'endsAt'] as const).map((name) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <input type="hidden" {...field} value={String(field.value ?? '')} />
                )}
              />
            ))}
            <div className="flex min-h-6 items-center justify-between gap-2">
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-neutral-600">
                {[
                  t('scheduling.phone.days', { count: includedDays.length }),
                  t('scheduling.phone.manHours', {
                    count: Math.round(manHours * includedDays.length * 10) / 10,
                  }),
                ].join(' · ')}
              </span>
              {!!conflictItems.length && (
                <button
                  type="button"
                  onClick={() => setConflictsOpen(true)}
                  className="flex h-7 shrink-0 items-center gap-1 rounded-full bg-warning-50 px-2.5 text-xs font-bold text-warning-700">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning-500" />
                  {t('scheduling.phone.conflicts', { count: conflictItems.length })}
                </button>
              )}
            </div>
            <div className="[&>div>div:last-child]:grid [&>div>div:last-child]:auto-cols-fr [&>div>div:last-child]:grid-flow-col [&>div>div:last-child]:gap-2 [&_button]:h-11 [&_button]:w-full [&_button]:justify-center">
              <InternalScheduleActions
                hideCancel
                onCancel={() => close()}
                onSendSchedule={() => {
                  if (isMulti && conflictItems.length) {
                    pendingAction.current = 'send';
                    setSendCheck(true);
                  } else send();
                }}
                onRescheduleAsDraft={() => {
                  setValue('status', 'draft');
                  setValue('actionType', '');
                  submit();
                }}
                onSaveAsDraft={() => {
                  rememberSent();
                  setValue('actionType', '');
                }}
                locked={locked || !active.schedule.id || !includedDays.length}
                scheduleId={current?.id}
                foremanId={active.schedule.foreman?.id}
                readOnly={readOnly}
                isDraft={isDraft}
              />
            </div>
          </form>
        }>
        <div className="flex flex-col gap-5 bg-white px-3 pb-4 pt-3">
          {isMulti ? (
            <div className="flex flex-col gap-1.5">
              <PhoneMultiDay
                value={multiDay}
                onChange={setMultiDay}
                dots={multiDots}
                disabled={disabled}
              />
              {originalDay && firstDay && firstDay !== originalDay && (
                <p className="rounded-md bg-warning-50 px-2.5 py-2 text-xs font-medium text-warning-700">
                  {t('scheduling.phone.movesTo', {
                    date: `${shortDay(parseISO(firstDay))}, ${shortDate(parseISO(firstDay))}`,
                  })}
                </p>
              )}
            </div>
          ) : (
            <PhoneDateBar
              date={rawDate}
              onChange={setRawDate}
              disabled={Boolean(schedulesFetching)}
              dots={monthDots}
              onMonthChange={setPopMonth}
              onOpenChange={(value) => !value && setPopMonth(undefined)}
            />
          )}
          <PhoneSection
            title={t('scheduling.phone.time')}
            right={
              <span className="text-xs text-neutral-500">
                {t('scheduling.phone.hours', { count: hours })}
              </span>
            }>
            <PhoneTimeRange
              value={range}
              onChange={activeSchedule ? setActiveTime : setDraftTime}
              disabled={disabled}
            />
          </PhoneSection>
          <div ref={fieldUserRef} className="scroll-mt-2">
            <PhoneFieldUser
              records={data?.records || []}
              withoutTeam={data?.withoutTeam || []}
              loading={schedulesLoading}
              activeSchedule={activeSchedule}
              window={slot}
              specialty={type?.value}
              disabled={disabled}
              onAssign={assign}
              multi={multiProps}
            />
          </div>
          <>
            <PhoneCrew
              open={picker === 'crew'}
              onOpenChange={(value) => setPicker(value ? 'crew' : null)}
              swapId={picker === 'crew' ? swapId : undefined}
              onSwap={setSwapId}
              multi={multiProps}
              specialty={type?.value}
            />
            <PhoneEquipment
              open={picker === 'equipment'}
              onOpenChange={(value) => setPicker(value ? 'equipment' : null)}
              swapId={picker === 'equipment' ? swapId : undefined}
              onSwap={setSwapId}
              multi={multiProps}
              specialty={type?.value}
            />
          </>
          <PhoneSection title={t('forms.note.label')}>
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <Textarea
                  disabled={readOnly}
                  placeholder={t('scheduling.phone.notePlaceholder')}
                  className="min-h-[68px] text-base"
                  {...field}
                />
              )}
            />
            {!readOnly && (
              <FormField
                control={form.control}
                name="confirmed"
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    id="mobile-confirmed"
                    className="h-6 w-6"
                    checked={Boolean(value)}
                    onCheckedChange={onChange}>
                    {t('schedules.markAsConfirmed')}
                  </Checkbox>
                )}
              />
            )}
          </PhoneSection>
        </div>
      </SchedulerShell>
      <PhoneConflictsSheet
        open={conflictsOpen || sendCheck}
        onDismiss={() => {
          setConflictsOpen(false);
          setSendCheck(false);
        }}
        items={conflictItems}
        locked={locked}
        onSendAnyway={
          sendCheck
            ? () => {
                setSendCheck(false);
                setValue('forceOverlapping', true);
                if (pendingAction.current === 'send') send();
                else submit();
              }
            : undefined
        }
      />
      <PhoneConflictCalendar
        target={calendarTarget}
        days={includedDays}
        onDismiss={() => setCalendarTarget(undefined)}
      />
      <PhoneDiscardSheet
        open={discardOpen}
        locked={locked || !active.schedule.id}
        onDismiss={() => setDiscardOpen(false)}
        onSaveDraft={readOnly ? undefined : saveDraft}
        onDiscard={() => close()}
      />
      {overlap && (
        <OverlapScheduleDialog
          sheet
          open={overlap}
          setOpen={setOverlap}
          disabled={locked || !active.schedule.id}
          onCancel={() => setValue('forceOverlapping', false)}
          onConfirm={() => {
            setValue('forceOverlapping', true);
            submit();
          }}
          conflicts={conflicts || []}
          date={
            active.schedule?.startsAt ? shortDayDate(toTenantDate(active.schedule.startsAt)) : ''
          }
          name={active.schedule?.task?.name}
          summaryDetail={
            active.schedule?.startsAt
              ? `${timeText}, ${shortDayDate(toTenantDate(active.schedule.startsAt))}`
              : ''
          }
        />
      )}
      <SpecialtyOverrideDialog
        sheet
        open={specialtyOverride}
        setOpen={setSpecialtyOverride}
        disabled={locked || !active.schedule.id}
        onCancel={() => setValue('forceSpecialty', false)}
        onConfirm={() => {
          setValue('forceSpecialty', true);
          submit();
        }}
      />
    </Form>
  );
}
